"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="h-8 w-32 rounded bg-muted/30 animate-pulse mb-8" />
        <div className="h-12 w-3/4 rounded bg-muted/30 animate-pulse mb-6" />
        <div className="h-96 w-full rounded-xl bg-muted/30 animate-pulse mb-8" />
        <div className="space-y-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-4 rounded bg-muted/30 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12 text-center">
        <p className="text-muted-foreground mb-4">Failed to load post content.</p>
        <Link to="/">
          <Button variant="outline">
            <ArrowLeft />
            Return Home
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12 text-center">
        <p className="text-muted-foreground mb-4">Post not found.</p>
        <Link to="/">
          <Button variant="outline">
            <ArrowLeft />
            Return Home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-4xl px-6 py-12">
      {}
      <div className="mb-8">
        <Link to="/">
          <Button variant="ghost" size="sm">
            <ArrowLeft />
            Back to Posts
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-8">
        <h1 className="font-display text-4xl font-bold text-foreground mb-6 leading-tight">
          {post.title}
        </h1>

        {}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span>{post.author.name}</span>
            </div>
          )}
        </div>

        {}
        {post.categories && post.categories.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {post.categories.map((category) => (
              <span
                key={category.id}
                className="inline-flex items-center rounded-md bg-primary-light text-primary px-3 py-1 text-xs font-medium"
              >
                {category.name}
              </span>
            ))}
          </div>
        )}
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-8 rounded-xl overflow-hidden shadow-medium">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {}
      <div
        className="prose prose-lg max-w-none mb-12 text-foreground"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-12" />

      {}
      {post.author && (
        <Card className="mb-12 bg-muted-light">
          <CardContent>
            <div className="flex items-start gap-4">
              <Avatar className="size-16 shrink-0">
                {post.author.avatar?.["96"] && (
                  <AvatarImage
                    src={post.author.avatar["96"]}
                    alt={post.author.name}
                  />
                )}
                <AvatarFallback className="text-lg font-semibold bg-primary text-primary-foreground">
                  {post.author.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-display text-lg font-semibold text-foreground mb-1">
                  About {post.author.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Expert contributor at Flex DPS
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mb-12">
          <h3 className="font-display text-lg font-semibold text-foreground mb-4">
            Tagged with:
          </h3>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center rounded-md bg-secondary-light text-secondary px-3 py-1.5 text-sm"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <Separator className="my-12" />

      {}
      <section>
        <h2 className="font-display text-2xl font-bold text-foreground mb-6">
          Related Articles
        </h2>
        <PostsProvider className="related-posts-grid" wp_query={{ per_page: 3, orderby: "date", order: "desc" }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "_embedded", "_links.wp:featuredmedia"]}>
          <RelatedPostsGrid />
        </PostsProvider>
      </section>
    </article>
  );
}

function RelatedPostsGrid() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-64 rounded-xl bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">No related posts available.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="card hover-lift" data-index={i}>
          {post.featuredImage && (
            <div className="rounded-t-xl overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-48 object-cover"
              />
            </div>
          )}
          <CardContent className="px-6">
            <h3 className="font-display text-base font-semibold text-foreground mb-2 line-clamp-2">
              {post.title}
            </h3>
            {post.excerpt && (
              <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                {post.excerpt.replace(/<[^>]*>/g, "")}
              </p>
            )}
            {post.link && (
              <Link to={post.link}>
                <Button variant="outline" size="sm" className="w-full">
                  Read More →
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();
  const currentPostType = wvcClient.getCurrentPostType();

  return (
    <div className="min-h-screen bg-light-background">
      <PostsProvider postType={currentPostType} wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>
    </div>
  );
}